import { AnimatePresence, motion } from 'framer-motion'
import React from 'react'
import {
  FaCheckCircle,
  FaExclamationCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaMagic,
  FaTimes
} from 'react-icons/fa'
import styled from 'styled-components'
import { useLocalStorage } from '@hooks'

const ICONS = {
  info: FaInfoCircle,
  success: FaCheckCircle,
  error: FaExclamationCircle,
  warning: FaExclamationTriangle,
  magic: FaMagic
}

export type AlertType = 'info' | 'success' | 'error' | 'warning' | 'magic'

interface Props {
  type?: AlertType
  title?: string | React.ReactElement
  description?: string | React.ReactElement
  isVisible?: boolean
  marginBottom?: number | string
  /** No border/padding — for embedding inside an already padded surface. */
  inline?: boolean
  dismissable?: boolean
  dismissableKeyDotPath?: string
  icon?: React.ComponentType
}

type DismissValue = 'dismissed' | undefined

export const Alert: React.FC<Props> = ({
  type = 'info',
  title,
  description,
  isVisible = true,
  marginBottom,
  inline = false,
  dismissable,
  dismissableKeyDotPath,
  icon
}) => {
  if (dismissable && !dismissableKeyDotPath) throw 'dismissableKeyDotPath required'

  const [isDismissed, setDismissed] = dismissable
    ? useLocalStorage<DismissValue>(dismissableKeyDotPath, undefined)
    : [null, null]

  const Icon = icon ?? ICONS[type]
  return (
    <AnimatePresence initial={false}>
      {isVisible && isDismissed !== 'dismissed' && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <StyledAlert
            className={type}
            aria-live="assertive"
            $marginBottom={marginBottom}
            $inline={inline}
          >
            <AlertContent>
              <AlertTitleRow $centerIcon={Boolean(title)}>
                <Icon />
                {title && <AlertTitle>{title}</AlertTitle>}
                {!title && description && <AlertText>{description}</AlertText>}
                {dismissable && (
                  <DismissButton onClick={() => (setDismissed as any)?.('dismissed')}>
                    <FaTimes />
                  </DismissButton>
                )}
              </AlertTitleRow>
              {title && description && <AlertText>{description}</AlertText>}
            </AlertContent>
          </StyledAlert>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

const AlertContent = styled.div`
  align-self: center;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  min-width: 0;
`

const AlertTitle = styled.div`
  flex: 1;
  font-weight: bold;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.neutralBlack};
`
const AlertText = styled.div`
  margin: 0;
  flex: 1;
  min-width: 0;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  overflow-wrap: anywhere;
  word-break: break-word;
`

const StyledAlert = styled.div<{ $marginBottom?: number | string; $inline?: boolean }>`
  --alert-accent: ${({ theme }) => theme.colors.info};
  padding: ${({ theme, $inline }) => ($inline ? 0 : `${theme.spacing.sm}rem`)};
  margin-bottom: ${({ $marginBottom, theme }) =>
    $marginBottom !== undefined ? $marginBottom : `${theme.spacing.md}rem`};
  border: ${({ theme, $inline }) => ($inline ? 'none' : `1px solid ${theme.colors.neutralGray}`)};
  border-radius: ${({ theme, $inline }) => ($inline ? 0 : `${theme.borderRadius.sm}rem`)};
  display: flex;
  color: ${({ theme }) => theme.colors.neutralBlack};
  gap: ${({ theme }) => theme.spacing.sm}rem;
  min-width: 0;

  & svg {
    width: ${({ theme }) => theme.fontSize.md}rem;
    height: ${({ theme }) => theme.fontSize.md}rem;
    min-width: ${({ theme }) => theme.fontSize.md}rem;
    min-height: ${({ theme }) => theme.fontSize.md}rem;
  }

  &.success {
    --alert-accent: ${({ theme }) => theme.colors.success};
  }

  &.info {
    --alert-accent: ${({ theme }) => theme.colors.info};
  }

  &.warning {
    --alert-accent: ${({ theme }) => theme.colors.warning};
  }

  &.error {
    --alert-accent: ${({ theme }) => theme.colors.error};
  }

  &.magic {
    --alert-accent: ${({ theme }) => theme.colors.AIPurple};
  }
`

const AlertTitleRow = styled.div<{ $centerIcon?: boolean }>`
  display: flex;
  flex: 1;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  align-items: ${({ $centerIcon }) => ($centerIcon ? 'center' : 'flex-start')};
  min-width: 0;

  > svg:first-child {
    color: var(--alert-accent);
    fill: var(--alert-accent);
  }
`

const DismissButton = styled.button`
  outline: none;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
`
